import { useMemo, useState } from 'react'
import { View, ScrollView } from 'react-native'
import Page from '@shell/Page'
import Typography from '@ui/typography'
import Select from '@ui/forms/Select'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import { useCustomer } from 'hooks/useSession'
import { trpc } from 'api/trpc'
import { useToast } from '@ui/toasts/useToastManager'
import { RegionCode, Regions } from '@ca/common'
import { displayCustomerName } from '@ca/common/types'

type ChannelType = 'email' | 'mobile'

const channelOptions = [
  { label: 'Email', value: 'email' as ChannelType },
  { label: 'Mobile (WhatsApp)', value: 'mobile' as ChannelType },
]

export default function DevNotificationsPage() {
  const customer = useCustomer()
  const toast = useToast()
  const [channel, setChannel] = useState<ChannelType>('email')
  const [url, setUrl] = useState('https://example.com/paylink')
  const [orderId, setOrderId] = useState('')
  const [testEmail, setTestEmail] = useState('')
  const [region, setRegion] = useState<RegionCode>('de')

  const regions = useMemo(
    () =>
      Object.entries(Regions).map(([key, value]) => ({
        label: value.name,
        value: key,
      })),
    []
  )

  const notifyMutation = trpc.customer.notify.useMutation({
    onSuccess: (_data, variables) => {
      const { channel: sentChannel, notification } = variables
      const message =
        notification.type === 'purchase-confirmation'
          ? 'Purchase confirmation email sent'
          : `Successfully sent ${sentChannel} notification`
      toast({
        variant: 'success',
        title: 'Notification sent',
        text: message,
      })
    },
    onError: (error) => {
      toast({
        variant: 'error',
        title: 'Failed to send notification',
        text: error.message,
      })
    },
  })

  const sendNotification = (
    notification:
      | { type: 'paylink'; url: string }
      | {
          type: 'purchase-confirmation'
          order: { orderId: string; orderDate: string; orderTotal: string }
        },
    overrideChannel?: ChannelType
  ) => {
    const effectiveChannel = overrideChannel ?? channel
    notifyMutation.mutate({
      channel: effectiveChannel,
      notification,
      region,
      ...(testEmail.trim() && { recipientEmail: testEmail.trim() }),
    })
  }

  const handleSendPaylink = () => {
    if (!url.trim()) {
      toast({
        variant: 'error',
        title: 'Validation error',
        text: 'URL is required',
      })
      return
    }
    sendNotification(
      {
        type: 'paylink',
        url: url.trim(),
      },
      channel
    )
  }

  const handleSendPurchaseConfirmation = () => {
    sendNotification(
      {
        type: 'purchase-confirmation',
        order: {
          orderId: orderId.trim() ?? 'DE-123456',
          orderDate: new Date().toISOString(),
          orderTotal: '123.45 €',
        },
      },
      'email'
    )
  }

  return (
    <Page stage="dev">
      <ScrollView className="flex-1">
        <View className="gap-8 p-16">
          <View className="gap-4">
            <Typography variant="LG">Notifications Test</Typography>
            <Typography variant="SM" className="color-desc">
              Send test notifications to your logged-in account
            </Typography>
          </View>

          {customer && (
            <View className="gap-4">
              <Typography variant="SMB">Customer Info</Typography>
              <View className="rounded-md border border-black/10 p-4">
                <Typography variant="SM">
                  {displayCustomerName(customer)}
                </Typography>
                <Typography variant="XS" className="mt-1 color-desc">
                  Email: {customer.email || 'N/A'}
                </Typography>
                <Typography variant="XS" className="mt-1 color-desc">
                  Mobile: {customer.mobile || 'N/A'}
                </Typography>
              </View>
            </View>
          )}

          <View className="gap-4">
            <Select
              label="Notification Channel"
              value={channel}
              onChange={(value) => setChannel(value as ChannelType)}
              options={channelOptions}
            />
            <Select
              label="Region"
              value={region}
              onChange={(value) => setRegion(value as RegionCode)}
              options={regions}
            />
            <Input
              label="Test email (override)"
              placeholder="e.g. test@example.com"
              value={testEmail}
              onChangeText={setTestEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          <View className="gap-4">
            <Typography variant="SMB">Paylink</Typography>
            <Input
              label="Paylink URL"
              placeholder="https://example.com/paylink"
              value={url}
              onChangeText={setUrl}
            />
            <View className="flex flex-row items-start gap-4 pt-2">
              <Button
                label="Send Paylink Notification"
                onPress={handleSendPaylink}
                loading={notifyMutation.isPending}
                disabled={
                  !(customer || testEmail.trim()) ||
                  notifyMutation.isPending ||
                  !url.trim()
                }
              />
            </View>
          </View>

          <View className="gap-4 border-t border-black/10 pt-8">
            <Typography variant="SMB">Purchase confirmation</Typography>
            <Input
              label="Order ID"
              placeholder="e.g. 123456"
              value={orderId}
              onChangeText={setOrderId}
            />
            <View className="flex flex-row items-start gap-4 pt-2">
              <Button
                label="Send Purchase Confirmation Email"
                onPress={handleSendPurchaseConfirmation}
                loading={notifyMutation.isPending}
                disabled={
                  !(customer || testEmail.trim()) ||
                  notifyMutation.isPending ||
                  !orderId.trim()
                }
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </Page>
  )
}
